import { useEffect, useState } from 'react'
import { api, ApiError, type AuditEntry } from '../api'
import { useAuth } from '../auth'
import { Topbar } from '../components/Topbar'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { EmptyState } from '../components/EmptyState'
import { useToast } from '../components/Toast'

function formatTs(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  // 2025-01-09 14:23:18 — local time, monospace-friendly
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    ` ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  )
}

function MetaLine({ entry }: { entry: AuditEntry }) {
  const parts: string[] = []
  if (entry.username) parts.push(entry.username)
  else if (entry.userId) parts.push(`user:${entry.userId.slice(0, 8)}`)
  else parts.push('system')
  if (entry.ip) parts.push(entry.ip)
  if (entry.meta && Object.keys(entry.meta).length > 0) {
    try {
      const j = JSON.stringify(entry.meta)
      if (j && j !== '{}') parts.push(j)
    } catch {
      /* ignore */
    }
  }
  return (
    <div className="audit-row-meta">
      {parts.join(' · ')}
      {entry.target && <span className="meta-target">→ {entry.target}</span>}
    </div>
  )
}

export function AdminAuditPage() {
  const { user: me } = useAuth()
  const toast = useToast()
  const [entries, setEntries] = useState<AuditEntry[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [prefix, setPrefix] = useState('')
  const [appliedPrefix, setAppliedPrefix] = useState('')

  async function refresh() {
    setLoading(true)
    try {
      const list = await api.listAudit({
        limit: 200,
        action: appliedPrefix || undefined,
      })
      setEntries(list)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        toast.show('Session expired — sign in again.', 'error')
      } else {
        toast.show(`Couldn't load audit (HTTP ${e instanceof ApiError ? e.status : '?'}).`, 'error')
      }
      setEntries([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedPrefix])

  if (me && !me.isOwner) {
    return (
      <div className="page">
        <Topbar />
        <main className="page-main">
          <EmptyState
            mark="restricted"
            title="Owner only"
            body="You need an owner account to view the audit log."
          />
        </main>
      </div>
    )
  }

  return (
    <div className="page">
      <Topbar />
      <main className="page-main">
        <div className="page-heading">
          <div>
            <h1>Audit log</h1>
            <div className="page-heading-sub">
              Most recent actions on the server. Reverse chronological.
            </div>
          </div>
          <Button variant="secondary" onClick={refresh} disabled={loading}>
            Refresh
          </Button>
        </div>

        <div className="audit-page">
          <div className="audit-filters">
            <Input
              label="Action prefix"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              hint="e.g. auth. / admin. / admin.user."
              className="audit-filter-input"
            />
            <Button
              variant="primary"
              onClick={() => setAppliedPrefix(prefix.trim())}
              disabled={loading}
            >
              Apply
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setPrefix('')
                setAppliedPrefix('')
              }}
              disabled={loading || (!prefix && !appliedPrefix)}
            >
              Clear
            </Button>
          </div>

          {loading && <div className="muted">Loading…</div>}

          {!loading && entries && entries.length === 0 && (
            <EmptyState
              mark="empty log"
              title="No matching entries"
              body={
                appliedPrefix
                  ? `Nothing logged with action prefix "${appliedPrefix}". Try a shorter prefix or clear the filter.`
                  : 'No audit entries yet. Trigger an action — sign in, create a user, edit a feature — and refresh.'
              }
            />
          )}

          {!loading && entries && entries.length > 0 && (
            <div className="audit-list">
              <div className="audit-row audit-header-row">
                <div className="audit-row-time audit-header-text">
                  when
                </div>
                <div className="audit-row-action audit-header-text">
                  action
                </div>
                <div className="audit-row-meta audit-header-text">
                  who · ip · target
                </div>
              </div>
              {entries.map((e) => (
                <div key={e.id} className="audit-row">
                  <div className="audit-row-time">{formatTs(e.createdAt)}</div>
                  <div className="audit-row-action">{e.action}</div>
                  <MetaLine entry={e} />
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
